import type { HairlineFigure } from "./Hairline";
import {
  Cam,
  circ,
  clamp,
  fillet,
  fit,
  poly,
  proj,
  rad,
  seg,
  unproj,
} from "./iso";
import { tdone, tset, tval, tween } from "./motion";
import {
  disposer,
  mk,
  pointer,
  register,
  type FigureEls,
  type FigureHandle,
} from "./stage";

/**
 * Keyring: six keys on a flat ring, fanned like a hand of cards, each the role
 * someone holds in a business. The pointer's angle round the ring picks a key:
 * it comes up off the fan, its tip tilted to you, and the keys beside it
 * part away from it along the ring, less the further away, staggered outwards
 * on the 700ms lift curve. Each key's bit is cut differently; the role's name
 * goes to the read-out. At rest the accountant's key lies on top of the
 * others, lifted a little, bright. The slider is how far the neighbours part, in degrees.
 *
 * The pattern: one of many, as Riffle, round a ring. Tweens of each key's angle,
 * tilt and height, a paint order kept by moving groups, and a hit test on the
 * keys' rest angles, read on the ground plane, which never moves.
 */

const ROLES = ["owner", "admin", "accountant", "bookkeeper", "viewer", "firm"];
/** Each key's bit, five teeth from the tip back, no two neighbours alike. */
const TEETH = [
  [5, 2, 5, 3.5, 2],
  [2, 5, 3.5, 5, 2],
  [3.5, 2, 5, 2, 5],
  [5, 3.5, 2, 5, 3.5],
  [2, 3.5, 5, 2, 3.5],
  [3.5, 5, 2, 3.5, 5],
];
const N = ROLES.length,
  RING = 14,
  BOW = 9,
  HOLE = 3.4,
  SW = 2.4,
  TIP = 62,
  TK = 1.2;
/** At rest: each key's angle round the ring (fanned away from the viewer, so a lifted key turns toward them), its height in the stack, and the one lifted a little. */
const REST = [-196, -172, -149, -124, -101, -76],
  Z = REST.map((_, k) => 2 + 1.2 * k),
  MARK = 2,
  TOP = 2 + 1.2 * N;
const TILT = 22,
  NUDGE = 8,
  LIFT = 2,
  FALL = [0, 1, 0.55, 0.25],
  STEP = 45,
  REACH = 22;

/** A key's outline in its own plane (s out from the bow's centre, t across): the bow, the shaft, the bit and the tip. */
function outline(teeth) {
  const pts = [],
    rs = [],
    a0 = Math.asin(SW / BOW);
  for (let i = 0; i <= 18; i++) {
    const a = a0 + (i / 18) * (2 * Math.PI - 2 * a0);
    pts.push([BOW * Math.cos(a), BOW * Math.sin(a)]);
    rs.push(0.01);
  }
  pts.push([TIP, -SW], [TIP, SW]);
  rs.push(1.6, 1);
  teeth.forEach((h, i) => {
    pts.push([56 - 4 * i, SW + h], [52 - 4 * i, SW + h]);
    rs.push(0.5, 0.5);
  });
  pts.push([36, SW]);
  rs.push(0.5);
  return fillet(pts, rs, 3);
}
const HOLE_PTS = circ(HOLE, 20)
  .map((q) => [q.u, q.v])
  .reverse();

function mount({ stage, svg, read }: FigureEls, value: number): FigureHandle {
  const bag = disposer();
  let part = value;

  const C = Cam(45, 0.5, 2.2);
  // Fitted to the fan parted as far as the slider goes, and to a key lifted at either end of it.
  const far = [];
  for (let a = REST[0] - REACH; a <= REST[N - 1] + REACH; a += 4)
    far.push([
      Math.cos(rad(a)) * (RING + TIP + 6),
      Math.sin(rad(a)) * (RING + TIP + 6),
      0,
    ]);
  for (const a of REST)
    far.push([
      Math.cos(rad(a)) * (RING + TIP * Math.cos(rad(TILT))),
      Math.sin(rad(a)) * (RING + TIP * Math.cos(rad(TILT))),
      TOP + LIFT + TIP * Math.sin(rad(TILT)),
    ]);
  fit(
    C,
    [
      [-RING - BOW, -RING - BOW, 0],
      [RING + BOW, RING + BOW, 0],
      [-RING - BOW, RING + BOW, 0],
      [RING + BOW, -RING - BOW, 0],
      ...far,
    ],
    200,
    166,
  );
  const P = proj(C);

  /** A point of a key at angle phi tilted th about its bow, standing at height z; `back` is its underside. */
  const at = (phi, th, z, back) => (s, t) => {
    const ux = Math.cos(phi),
      uy = Math.sin(phi),
      r = RING + s * Math.cos(th) + (back ? TK * Math.sin(th) : 0);
    return P(
      r * ux - t * uy,
      r * uy + t * ux,
      z + s * Math.sin(th) - (back ? TK * Math.cos(th) : 0),
    );
  };

  const g = mk("g", {}, svg);
  // The ring, under every key: a flat band, seen again through each bow's hole.
  const band = (r0, r1, z) =>
    poly(circ(r0, 64).map((q) => P(q.u, q.v, z))) +
    poly(
      circ(r1, 64)
        .map((q) => P(q.u, q.v, z))
        .reverse(),
    );
  mk("path", { d: band(RING + 0.9, RING - 0.9, 0), class: "lo" }, g);
  mk("path", { d: band(RING + 0.9, RING - 0.9, 1.2), class: "sil" }, g);

  const keys = ROLES.map((_, k) => {
    const grp = mk("g", {}, g);
    return {
      k,
      grp,
      shape: outline(TEETH[k]),
      back: mk("path", { class: "lo" }, grp),
      face: mk("path", { class: "sil" }, grp),
      groove: mk("path", { class: "nf lo" }, grp),
      a: tween(REST[k]),
      th: tween(k === MARK ? NUDGE : 0),
      z: tween(k === MARK ? TOP : Z[k]),
      drawn: "",
    };
  });

  function draw(key, now) {
    const a = tval(key.a, now),
      th = tval(key.th, now),
      z = tval(key.z, now),
      sig = `${a}|${th}|${z}`;
    if (sig === key.drawn) return;
    key.drawn = sig;
    const w = at(rad(a), rad(th), z, false),
      wb = at(rad(a), rad(th), z, true);
    key.back.setAttribute(
      "d",
      poly(key.shape.map(([s, t]) => wb(s, t))) +
        poly(HOLE_PTS.map(([s, t]) => wb(s, t))),
    );
    key.face.setAttribute(
      "d",
      poly(key.shape.map(([s, t]) => w(s, t))) +
        poly(HOLE_PTS.map(([s, t]) => w(s, t))),
    );
    key.groove.setAttribute("d", seg(w(BOW + 3, -0.6), w(TIP - 4, -0.6)));
  }

  let order = "";
  /** Bottom to top by each key's height now: flat plates at different heights, so this is the order they cover each other in. */
  function sort(now) {
    const by = keys.slice().sort((p, q) => tval(p.z, now) - tval(q.z, now));
    const key = by.map((p) => p.k).join(",");
    if (key !== order) {
      order = key;
      g.append(...by.map((p) => p.grp));
    }
  }

  const B = register(stage, (_dt, now) => {
    let moving = false;
    for (const key of keys) {
      draw(key, now);
      if (!tdone(key.a, now) || !tdone(key.th, now) || !tdone(key.z, now))
        moving = true;
    }
    sort(now);
    return moving;
  });
  bag.add(() => B.unregister());

  /** The key whose rest angle is nearest the pointer's, each read on the plane of its own rest height round the ring; -1 off the fan. */
  function hit([sx, sy]) {
    let best = -1,
      off = 24;
    REST.forEach((r, k) => {
      const [wx, wy] = unproj(C, sx, sy, Z[k]),
        d = Math.hypot(wx, wy),
        diff = Math.abs(
          (((((Math.atan2(wy, wx) * 180) / Math.PI - r) % 360) + 540) % 360) -
            180,
        );
      if (d > RING - BOW && d < RING + TIP + 12 && diff < off) {
        off = diff;
        best = k;
      }
    });
    return best;
  }

  let act = -1;
  /** Lifts key a (-1 lays them all back): its neighbours part away from it along the ring, later the further away. */
  function setActive(a) {
    if (a === act) return;
    const now = performance.now(),
      from = a >= 0 ? a : act;
    act = a;
    for (const key of keys) {
      const far = Math.abs(key.k - from),
        delay = far * STEP,
        side = Math.sign(key.k - a);
      tset(
        key.a,
        a < 0
          ? REST[key.k]
          : REST[key.k] +
              side *
                clamp(part, 0, REACH) *
                FALL[clamp(Math.abs(key.k - a), 0, 3)],
        now,
        delay,
      );
      tset(
        key.th,
        a < 0 ? (key.k === MARK ? NUDGE : 0) : key.k === a ? TILT : 0,
        now,
        delay,
      );
      tset(
        key.z,
        a < 0
          ? key.k === MARK
            ? TOP
            : Z[key.k]
          : key.k === a
            ? TOP + LIFT
            : Z[key.k],
        now,
        delay,
      );
      key.face.classList.toggle("hi", a < 0 ? key.k === MARK : key.k === a);
    }
    read.textContent = a < 0 ? "rest" : ROLES[a];
    B.wake();
  }

  keys[MARK].face.classList.add("hi");
  bag.add(
    pointer(stage, {
      move: (p) => setActive(hit(p)),
      leave: () => setActive(-1),
    }),
  );
  bag.add(() => svg.replaceChildren());

  return {
    set: (v) => {
      part = v;
      if (act >= 0) {
        const a = act;
        act = -1;
        setActive(a);
      }
    },
    destroy: () => bag.dispose(),
  };
}

export const KEYRING: HairlineFigure = {
  name: "keyring",
  means:
    "Six keys on a ring, one for each role in a business: the one the pointer points to lifts off the fan, and the keys beside it part.",
  range: [6, 14, 22],
  tour: [[255, 166], [116, 192], [177, 164], null],
  mount,
};
